import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { Button, Accordion, Form, Col, Spinner } from "react-bootstrap";
import * as formik from "formik";
import * as Yup from "yup";
import Credentials from "../components/signup-form/Credentials";
import PersonalData from "../components/signup-form/PersonalData";
import Residence from "../components/signup-form/Residence";
import Domicile from "../components/signup-form/Domicile";
import AuthLogo from "../components/AuthLogo";
import { Link } from "react-router-dom";
import {
  useCheckAffiliateCodeMutation,
  useCheckEmailExistsMutation,
  useRegisterMutation,
} from "../app/features/apiSlice";
import {
  passwordRegExp,
  emailRegex,
  alphanumericNoSpaces,
  getReferralFromUrl,
  swalError,
  swalSuccess,
  regInitialValues,
  userSchemaObject,
} from "../helpers";
import TermsAndConditions from "../components/signup-form/TermsAndConditions";
import ClientTermsAndConditions from "../components/signup-form/ClientTermsAndConditions";

function SignUp() {
  const { Formik } = formik;
  const formikRef = useRef();
  const { t } = useTranslation();
  const { lang } = useParams();
  const navigate = useNavigate();
  const [validEmail, setValidEmail] = useState({
    status: false,
    value: "",
  });
  const [validCode, setValidCode] = useState({
    status: false,
    value: "",
  });
  const referralCode = getReferralFromUrl();
  const initialValues = {
    ...regInitialValues,
    affiliateCode: referralCode ?? "",
    language: lang,
    roleId: referralCode ? "10" : "9",
  };

  const [checkEmailExists] = useCheckEmailExistsMutation();
  const checkEmailExistence = async (email) => {
    try {
      const response = await checkEmailExists({ email });
      return response.data.message === "exists" ? true : false;
    } catch (err) {
      console.log(err);
    }
  };

  const [checkAffiliateCode] = useCheckAffiliateCodeMutation();
  const validateAffiliateCode = async (affiliate_code) => {
    try {
      const response = await checkAffiliateCode({ affiliate_code });
      return response.data.message === "exists" ? true : false;
    } catch (err) {
      console.log(err);
    }
  };

  const schema = Yup.object().shape({
    ...userSchemaObject,
    email: Yup.string()
      .email("invalidEmail")
      .required("emailRequired")
      .test("unique-email", "emailAlreadyExists", async (value) => {
        if (!value) return true; // Skip validation if value is empty
        if (emailRegex.test(value)) {
          if (validEmail.value === value) {
            return !validEmail.status;
          } else {
            const emailExists = await checkEmailExistence(value);
            setValidEmail({ status: emailExists, value });
            return !emailExists;
          }
        }
      }),
    password: Yup.string()
      .required("passwordRequired")
      .matches(passwordRegExp, "invalidPassword"),
    confirmPassword: Yup.string()
      .required("confirmPasswordRequired")
      .oneOf([Yup.ref("password"), null], "passwordMismatch"),
    affiliateCode: Yup.string()
      .required("affiliateCodeRequired")
      .test("affiliate-code", "inValidAffiliateCode", async function (value) {
        if (!value) return true; // Skip validation if value is empty
        if (value.length > 2) {
          if (validCode.value === value) {
            return validCode.status;
          } else {
            const isExists = await validateAffiliateCode(value);
            setValidCode({ status: isExists, value });
            return isExists;
          }
        } else {
          return false;
        }
      })
      .matches(alphanumericNoSpaces, "specialCharacterNotAllowed"),
  });

  const [register, { isLoading }] = useRegisterMutation();
  const handleSubmit = async (values) => {
    try {
      const response = await register(values);
      if (response?.error) {
        console.log(response);
        swalError(response, t("error.serverNotResponding"));
      } else {
        swalSuccess(response?.data?.msg);
        formikRef.current.resetForm();
        setTimeout(() => {
          const redirectUrl =
            values.roleId === "10"
              ? `/${lang}/auth/verify-otp/${values.email}`
              : `/${lang}/login`;
          navigate(redirectUrl);
        }, 3000);
      }
    } catch (err) {
      swalError(err, t("error.serverNotResponding"));
    }
  };

  return (
    <Col lg="6" md="10" sm="12">
      <AuthLogo title="registerToAccess" />
      <Link to={`/${lang}/auth/verify-otp/jamtech717@gmail.com`}>Click</Link>
      <Formik
        innerRef={formikRef}
        validationSchema={schema}
        onSubmit={(values) => handleSubmit(values)}
        initialValues={initialValues}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur, //Validation is working on onBlur
          values,
          touched,
          errors,
          setFieldValue,
          setFieldTouched,  
          setValues,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Accordion>
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <small>Step 1</small>
                  <span>{t("signup.credentials")}</span>
                </Accordion.Header>
                <Accordion.Body>
                  <Credentials
                    formik={{
                      values,
                      handleChange,
                      touched,
                      errors,
                      handleBlur,
                      setFieldValue,
                      setFieldTouched,
                    }}
                  />
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="1">
                <Accordion.Header>
                  <small>Step 2</small>
                  <span>{t("signup.personalData")}</span>
                </Accordion.Header>
                <Accordion.Body>
                  <PersonalData
                    formik={{
                      values,
                      handleChange,
                      touched,
                      errors,
                      setFieldValue,
                      handleBlur,
                      setFieldValue,     
                      setFieldTouched,
                    }}
                  />
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="2">
                <Accordion.Header>
                  <small>Step 3</small>
                  <span>{t("signup.residence")}</span>
                </Accordion.Header>
                <Accordion.Body>
                  <Residence
                    formik={{
                      values,
                      handleChange,
                      touched,
                      errors,
                      handleBlur,
                      setFieldValue,      
                      setFieldTouched,
                    }}
                  />
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="3">
                <Accordion.Header>
                  <small>Step 4</small>
                  <span>{t("signup.domicile")}</span>
                </Accordion.Header>
                <Accordion.Body>
                  <Domicile
                    formik={{
                      values,
                      handleChange,
                      touched,
                      errors,
                      handleBlur,
                      setValues,
                    }}
                  />
                </Accordion.Body>
              </Accordion.Item>
              {values.roleId === "9" && (
                <Accordion.Item eventKey="4">
                  <Accordion.Header>
                    <small>Step 5</small>
                    <span>{t("termsAndConditions2")}</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <ClientTermsAndConditions
                      formik={{
                        values,
                        handleChange,
                        touched,
                        errors,
                        handleBlur,
                        setValues,
                        setFieldValue,
                      }}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              )}
              {values.roleId === "10" && (
                <Accordion.Item eventKey="4">
                  <Accordion.Header>
                    <small>Step 5</small>
                    <span>{t("termsAndConditions2")}</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <TermsAndConditions
                      formik={{
                        values,
                        handleChange,
                        touched,
                        errors,
                        handleBlur,
                        setValues,
                        setFieldValue,
                      }}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              )}
            </Accordion>
            <Button
              variant="primary"
              type="submit"
              className="mt-5"
              disabled={isLoading}
            >
              {t("register")}
              {isLoading && (
                <Spinner size="sm" className="ms-2" animation="grow" />
              )}
            </Button>
            <p className="m-0 mt-3 text-center">
              {t("haveAccount")}?{" "}
              <Link to={`/${lang}/login`} className="text-gold">
                {t("login")}
              </Link>
            </p>
          </Form>
        )}
      </Formik>
    </Col>
  );
}

export default SignUp;
